import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, X, Plus } from 'lucide-react';
import { PollarBrandMini } from '@/components/PollarBrand';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuestionnaireEdit = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Editable fields
  const [topic, setTopic] = useState('');
  const [hashtags, setHashtags] = useState([]);
  const [hashtagInput, setHashtagInput] = useState('');
  const [timeLimit, setTimeLimit] = useState(5);
  const [isAnonymous, setIsAnonymous] = useState(true);
  const [maxParticipants, setMaxParticipants] = useState(10);
  const [endDatetime, setEndDatetime] = useState('');
  const [showScores, setShowScores] = useState(false);
  const [allowRetry, setAllowRetry] = useState(false);
  
  // Read-only (questions)
  const [questions, setQuestions] = useState([]);

  const timeLimitOptions = [
    { value: 5, label: '5min' },
    { value: 10, label: '10min' },
    { value: 20, label: '20min' },
    { value: 30, label: '30min' },
    { value: 60, label: '60min' },
    { value: 0, label: 'No Limit' }
  ];

  useEffect(() => {
    fetchSession();
  }, [sessionId]);

  const fetchSession = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const session = response.data;
      setTopic(session.topic);
      setHashtags(session.hashtags || []);
      setTimeLimit(Math.floor(session.time_limit / 60));
      setIsAnonymous(session.is_anonymous);
      setMaxParticipants(session.max_participants);
      setEndDatetime(session.end_datetime || '');
      setShowScores(session.show_scores || false);
      setAllowRetry(session.allow_retry || false);
      setQuestions(session.questions || []);
      
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load questionnaire');
      navigate('/dashboard');
    }
  };

  const addHashtag = () => {
    if (hashtagInput.trim() && !hashtags.includes(hashtagInput.trim())) {
      setHashtags([...hashtags, hashtagInput.trim()]);
      setHashtagInput('');
    }
  };

  const removeHashtag = (tag) => {
    setHashtags(hashtags.filter(t => t !== tag));
  };

  const handleSave = async () => {
    if (!topic.trim()) {
      toast.error('Please enter a topic');
      return;
    }

    if (hashtags.length === 0) {
      toast.error('Please add at least one hashtag');
      return;
    }

    setSaving(true);

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/sessions/${sessionId}`,
        {
          topic,
          hashtags,
          time_limit: timeLimit * 60,
          is_anonymous: isAnonymous,
          max_participants: maxParticipants,
          end_datetime: endDatetime || null,
          show_scores: showScores,
          allow_retry: allowRetry
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('Questionnaire updated successfully!');
      navigate(`/facilitator/${sessionId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update questionnaire');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)' }}>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)' }}>
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="flex justify-between items-center mb-4">
          <PollarBrandMini />
        </div>
        
        <Button
          onClick={() => navigate(`/facilitator/${sessionId}`)}
          variant="ghost"
          className="mb-6"
        >
          <ArrowLeft className="mr-2 h-5 w-5" />
          Back to Results
        </Button>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          <h1 className="text-3xl font-bold mb-2" style={{ color: '#1a1a1a' }}>
            Edit Questionnaire Settings
          </h1>
          <p className="text-gray-600 mb-8">
            Update questionnaire details (questions are locked)
          </p>

          {/* Editable Fields */}
          <div className="mb-6">
            <Label htmlFor="topic">Questionnaire Title</Label>
            <Input
              id="topic"
              type="text"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              className="mt-1"
            />
          </div>

          <div className="mb-6">
            <Label>Hashtags</Label>
            <div className="flex gap-2 mt-1">
              <Input
                type="text"
                value={hashtagInput}
                onChange={(e) => setHashtagInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addHashtag();
                  }
                }}
                placeholder="Add hashtag and press Enter"
                className="flex-1"
              />
              <Button type="button" onClick={addHashtag} variant="outline">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {hashtags.map((tag, index) => (
                <Badge key={index} variant="secondary" className="flex items-center gap-1">
                  #{tag}
                  <X
                    className="w-3 h-3 cursor-pointer"
                    onClick={() => removeHashtag(tag)}
                  />
                </Badge>
              ))}
            </div>
          </div>

          <div className="mb-6">
            <Label className="text-sm text-gray-600 mb-2">Time Limit (to fill questionnaire)</Label>
            <div className="flex gap-2 flex-wrap">
              {timeLimitOptions.map((option) => (
                <Button
                  key={option.value}
                  type="button"
                  variant={timeLimit === option.value ? "default" : "outline"}
                  onClick={() => setTimeLimit(option.value)}
                >
                  {option.label}
                </Button>
              ))}
            </div>
          </div>

          <div className="mb-6">
            <Label>Submission Deadline (Optional)</Label>
            <p className="text-xs text-gray-500 mb-2">Latest time questionnaire can be submitted</p>
            <Input
              type="datetime-local"
              value={endDatetime}
              onChange={(e) => setEndDatetime(e.target.value)}
              className="mt-1"
            />
          </div>

          <div className="mb-6 space-y-4">
            <div>
              <Label>Maximum Participants: {maxParticipants}</Label>
              <input
                type="range"
                min="10"
                max="100"
                value={maxParticipants}
                onChange={(e) => setMaxParticipants(parseInt(e.target.value))}
                className="w-full"
              />
            </div>
            
            <div className="flex items-center justify-between">
              <Label>Anonymous Responses</Label>
              <Switch
                checked={isAnonymous}
                onCheckedChange={setIsAnonymous}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <Label>Show Scores to Participants</Label>
              <Switch
                checked={showScores}
                onCheckedChange={setShowScores}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <Label>Allow Retry</Label>
                <p className="text-xs text-gray-500">Let participants retake questionnaire</p>
              </div>
              <Switch
                checked={allowRetry}
                onCheckedChange={setAllowRetry}
              />
            </div>
          </div>

          {/* Read-only Questions Display */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <Label className="text-lg font-semibold mb-3 block">
              Questions ({questions.length}) - Read Only
            </Label>
            <p className="text-sm text-gray-600 mb-4">
              Questions cannot be edited once questionnaire is active
            </p>
            {questions.map((q, index) => (
              <div key={q.id} className="mb-2 p-3 bg-white rounded border">
                <p className="font-medium text-sm">
                  {index + 1}. {q.text}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Type: {q.response_type.replace(/_/g, ' ')}
                </p>
              </div>
            ))}
          </div>

          {/* Save Button */}
          <div className="flex gap-4">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="flex-1"
              style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button
              onClick={() => navigate(`/facilitator/${sessionId}`)}
              variant="outline"
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestionnaireEdit;
